import React, { useState, useEffect } from 'react';
import {
    Box, Typography, TextField, Button, Dialog, DialogContent, DialogActions,
    CircularProgress, FormControlLabel, Checkbox, Alert,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import { solidBtn } from './uiKit';
import { PRIMARY, PRIMARY_LIGHT, PRIMARY_BORDER } from '../theme';

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '7px', fontSize: '13px', bgcolor: '#F9FAFB',
        '&:hover': { bgcolor: '#fff' }, '&.Mui-focused': { bgcolor: '#fff' },
        '& fieldset': { borderColor: '#E5E7EB' },
        '&.Mui-focused fieldset': { borderColor: PRIMARY, borderWidth: '1.5px' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY },
};
const ghostSx = {
    textTransform: 'none', fontSize: '13px', fontWeight: 600, color: '#374151', borderRadius: '7px',
    border: '1px solid #E5E7EB', px: 2, height: 38, '&:hover': { bgcolor: '#F9FAFB' },
};

// Header shared by the salary-security popups: icon tile, title + subtitle, close button.
export function DialogHeader({ icon: Icon = LockOutlinedIcon, title, subtitle, onClose, disabled }) {
    return (
        <Box sx={{ px: 2.5, py: 2, background: `linear-gradient(135deg, ${PRIMARY_LIGHT} 0%, #fff 70%)`, borderBottom: `1px solid ${PRIMARY_BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '7px', bgcolor: '#fff', border: `1px solid ${PRIMARY_BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon sx={{ color: PRIMARY, fontSize: 20 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#111827', lineHeight: 1.2 }}>{title}</Typography>
                    {subtitle && <Typography sx={{ fontSize: 11.5, color: '#6B7280', mt: 0.3 }}>{subtitle}</Typography>}
                </Box>
            </Box>
            {onClose && (
                <IconButton size="small" onClick={onClose} disabled={disabled} sx={{ alignSelf: 'flex-start' }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
            )}
        </Box>
    );
}

// A small form dialog for secrets (password / recovery key): masked inputs with a "show typing" toggle.
// fields: [{ key, label, helper? }]. onSubmit receives { [key]: value }.
export default function SecretDialog({ open, icon, title, subtitle, fields, submitLabel, busy, error, onClose, onSubmit, children }) {
    const [values, setValues] = useState({});
    const [show, setShow] = useState(false);
    useEffect(() => { if (open) { setValues({}); setShow(false); } }, [open]);
    const ready = fields.every((f) => (values[f.key] || '').length > 0);
    const submit = () => ready && !busy && onSubmit(values);
    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: '7px', overflow: 'hidden' } } }}>
            <DialogHeader icon={icon} title={title} subtitle={subtitle} onClose={onClose} disabled={busy} />
            <DialogContent sx={{ px: 2.5, pt: '20px !important', pb: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {error && <Alert severity="error" sx={{ fontSize: 12, borderRadius: '7px' }}>{error}</Alert>}
                {fields.map((f, i) => (
                    <TextField
                        key={f.key} label={f.label} size="small" fullWidth autoFocus={i === 0}
                        type={show ? 'text' : 'password'} autoComplete="off" value={values[f.key] || ''}
                        helperText={f.helper}
                        onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                        slotProps={{ inputLabel: { shrink: true } }} sx={fieldSx}
                    />
                ))}
                <FormControlLabel
                    sx={{ m: 0, mt: -0.5 }}
                    control={<Checkbox size="small" checked={show} onChange={(e) => setShow(e.target.checked)} sx={{ p: 0.6, mr: 0.4 }} />}
                    label={<Typography sx={{ fontSize: 12, color: '#6B7280' }}>Show typing</Typography>}
                />
                {children}
            </DialogContent>
            <DialogActions sx={{ px: 2.5, py: 1.8, mt: 0.5, borderTop: '1px solid #E5E7EB', gap: 1 }}>
                <Button onClick={onClose} disabled={busy} sx={ghostSx}>Cancel</Button>
                <Button
                    onClick={submit} disabled={!ready || busy}
                    startIcon={busy ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
                    sx={{ ...solidBtn, px: 2.5, height: 38, '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' } }}
                >
                    {submitLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
